import { type Reference } from '@apollo/client'
import { Dialog } from '@factorialco/f0-react/dist/experimental'
import { EngagementDeleteSurveyDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  surveyId: string
  onSuccess: () => void
  onCancel: () => void
}

export function DeleteSurveyDialog({ surveyId, onSuccess, onCancel }: Props) {
  const [deleteSurvey] = useMutation(EngagementDeleteSurveyDocument, {
    extractorFn: (data) => data.surveysMutations.deleteSurveysSurvey,
    update(cache, { data }) {
      const deletedSurvey = data?.surveysMutations.deleteSurveysSurvey.survey
      if (!deletedSurvey) return

      const normalizedId = cache.identify({
        id: deletedSurvey.id,
        __typename: 'SurveysSurvey',
      })

      cache.modify({
        id: 'ROOT_QUERY',
        fields: {
          surveys(existing, { readField }) {
            const connection = readField<{
              edges: readonly unknown[]
              totalCount: number
            }>('surveysConnection', existing)

            if (!connection?.edges) return existing

            const filteredEdges = connection.edges.filter((edge) => {
              const node = readField<Reference>('node', edge as Reference)
              return node?.__ref !== normalizedId
            })

            return {
              ...existing,
              surveysConnection: {
                ...connection,
                edges: filteredEdges,
                totalCount: (connection.totalCount ?? 1) - 1,
              },
            }
          },
        },
      })

      if (normalizedId) {
        cache.evict({ id: normalizedId })
        cache.gc()
      }
    },
  })

  return (
    <Dialog
      onClose={onCancel}
      actions={{
        primary: {
          label: i18n.t('common.delete'),
          variant: 'critical',
          onClick: async () => {
            const response = await deleteSurvey({
              variables: {
                surveyId,
              },
            })

            const handled = handleResult(response, () => {})

            if (handled.success) {
              onSuccess()
            }
          },
        },
        secondary: {
          label: i18n.t('common.cancel'),
          onClick: onCancel,
        },
      }}
      header={{
        title: i18n.t('surveys.delete_survey_dialog.title'),
        description: i18n.t('surveys.delete_survey_dialog.description'),
        type: 'critical',
      }}
      open
    />
  )
}
